import Image from 'next/image';
import type { CSSProperties } from 'react';

import type { AiService } from '@/components/apps/pages/solution/industrial/industrialCaseShare';
import { IconArrowUpRightRound } from '@/components/apps/ui/icon';

export const AI_SERVICES: Array<{
  id: AiService;
  icon: string;
  hoverColor: string;
}> = [
  {
    id: 'chatgpt',
    icon: '/images/apps/solution/industrial/ai-services/chatgpt.svg',
    hoverColor: '#0b7a65',
  },
  {
    id: 'claude',
    icon: '/images/apps/solution/industrial/ai-services/claude.svg',
    hoverColor: '#a9482c',
  },
  {
    id: 'perplexity',
    icon: '/images/apps/solution/industrial/ai-services/perplexity.svg',
    hoverColor: '#176b73',
  },
  {
    id: 'gemini',
    icon: '/images/apps/solution/industrial/ai-services/gemini.svg',
    hoverColor: '#3157c8',
  },
];

type ServiceButtonStyle = CSSProperties & {
  '--service-hover-color': string;
};

type Props = {
  service: (typeof AI_SERVICES)[number];
  label: string;
  href?: string;
  onClick?: () => void;
  isInteractive?: boolean;
  newWindowLabel?: string;
};

export function AiServiceAction({
  service,
  label,
  href,
  onClick,
  isInteractive = true,
  newWindowLabel,
}: Props) {
  const style = {
    '--service-hover-color': service.hoverColor,
  } as ServiceButtonStyle;
  const content = (
    <>
      <span
        className="pointer-events-none absolute inset-y-0 -right-12 -left-12 z-0 -translate-x-[115%] -skew-x-12 bg-[var(--service-hover-color)] transition-transform duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/ai:translate-x-0 group-focus-visible/ai:translate-x-0 motion-reduce:transition-none"
        aria-hidden="true"
      />
      <span className="relative z-1 flex min-w-0 items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center bg-transparent">
          <Image
            alt=""
            src={service.icon}
            width={24}
            height={24}
            className={
              service.id === 'perplexity'
                ? 'mix-blend-screen'
                : 'brightness-0 invert'
            }
            aria-hidden="true"
          />
        </span>
        <span className="text-base">{label}</span>
      </span>
      <IconArrowUpRightRound
        className="relative z-1 text-white transition-transform duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/ai:translate-x-0.5 group-hover/ai:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none"
        size={32}
        aria-hidden="true"
      />

      {newWindowLabel && <span className="sr-only">{newWindowLabel}</span>}
    </>
  );
  const className =
    'group/ai relative isolate flex min-h-14 touch-manipulation cursor-pointer items-center justify-between overflow-hidden bg-[#151514] px-4 text-left text-sm font-semibold text-white transition-transform duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apps-brandGrayBlack motion-reduce:transform-none motion-reduce:transition-none';
  if (href)
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={style}
        onClick={onClick}
      >
        {content}
      </a>
    );
  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={isInteractive ? onClick : undefined}
      aria-disabled={!isInteractive}
      tabIndex={isInteractive ? 0 : -1}
    >
      {content}
    </button>
  );
}
